import type { ThreeElements } from '@react-three/fiber'
import type { ReactNode } from 'react'
import type * as THREE from 'three'
import { PREFIXO_SELECIONAVEL } from '../camera/comandosCamera'
import { useMaquete, usePeca } from './contextoMaquete'
import { resolverHitVeiculo } from '../inspecao/resolverHitVeiculo'

type PropsGrupo = ThreeElements['group']

// `id` do Object3D é numérico: o id didático da peça não pode colidir com ele
export interface PecaProps extends Omit<PropsGrupo, 'children' | 'id'> {
  id: string
  children: ReactNode
  /** deslocamento aplicado quando o estado é `afastada` (explosão didática) */
  deslocamentoAfastado?: [number, number, number]
  /** rotação aplicada quando o estado é `aberta`, em radianos */
  aberturaRad?: number
  /** eixo dessa rotação: Y para portas, X para encostos que reclinam */
  eixoAbertura?: 'x' | 'y'
  /** peças puramente decorativas não entram na seleção */
  selecionavel?: boolean
}

/**
 * Peça selecionável da maquete.
 *
 * Toda a lógica de estado vem do contexto: a peça só decide como o estado
 * recebido se traduz em transformação. `onPointerDown` cobre mouse e toque,
 * porque o R3F entrega eventos de ponteiro unificados.
 */
export function Peca({
  id,
  children,
  deslocamentoAfastado = [0, 0, 0],
  aberturaRad = 0,
  eixoAbertura = 'y',
  selecionavel = true,
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  ...resto
}: PecaProps) {
  const { estado, visivel, selecionar, capturaClique, categoria, aoRegistrarHit } = usePeca(id)
  const { explodido } = useMaquete()
  if (!visivel) return null

  const [px, py, pz] = position as [number, number, number]
  const [rx, ry, rz] = rotation as [number, number, number]
  const temDeslocamento =
    deslocamentoAfastado &&
    (deslocamentoAfastado[0] !== 0 || deslocamentoAfastado[1] !== 0 || deslocamentoAfastado[2] !== 0)
  // O monobloco (estrutura) permanece no centro como referência espacial na vista explodida
  const afastaNaExplosao = Boolean(explodido) && Boolean(temDeslocamento) && categoria !== 'estrutura'
  const afastada = estado === 'afastada' || afastaNaExplosao
  const aberta = estado === 'aberta'

  return (
    <group
      {...resto}
      // Nome estável para que a câmera consiga enquadrar a peça selecionada.
      // Só identifica; não muda geometria nem comportamento didático.
      name={`${PREFIXO_SELECIONAVEL}${id}`}
      position={[
        px + (afastada ? deslocamentoAfastado[0] : 0),
        py + (afastada ? deslocamentoAfastado[1] : 0),
        pz + (afastada ? deslocamentoAfastado[2] : 0),
      ]}
      rotation={[
        rx + (aberta && eixoAbertura === 'x' ? aberturaRad : 0),
        ry + (aberta && eixoAbertura === 'y' ? aberturaRad : 0),
        rz,
      ]}
      onPointerDown={
        selecionavel && (capturaClique || Boolean(aoRegistrarHit) || Boolean(resto.onPointerDown))
          ? (evento) => {
              evento.stopPropagation()
              const hit = resolverHitVeiculo(evento as unknown as THREE.Intersection)
              if (hit && aoRegistrarHit) {
                aoRegistrarHit(hit)
              }
              if (capturaClique) {
                selecionar(id)
              }
              if (typeof resto.onPointerDown === 'function') {
                (resto.onPointerDown as (e: unknown) => void)(evento)
              }
            }
          : undefined
      }
    >
      {children}
    </group>
  )
}

/**
 * Acabamento físico da peça.
 *
 * `fosco` é o padrão de propósito: peça didática — zona de absorção, SRS, cinto,
 * estrutura realçada — não pode virar espelho e competir com a cor semântica.
 * Quem ensina é a cor, não o reflexo. Só chapa externa e vidro ganham brilho.
 */
export type Acabamento = 'fosco' | 'pintura' | 'metal'

const ACABAMENTOS = {
  fosco: { metalico: 0.15, rugosidade: 0.55, ambiente: 0.35 },
  // metal alto com verniz por cima é como pintura automotiva se comporta:
  // um reflexo difuso da base e um segundo reflexo nítido do verniz
  pintura: { metalico: 0.75, rugosidade: 0.28, ambiente: 1 },
  metal: { metalico: 0.92, rugosidade: 0.16, ambiente: 1.15 },
} as const

/**
 * Material derivado do estado da peça. Existe para que nenhum mesh escreva
 * cor, opacidade ou realce por conta própria.
 */
export function MaterialPeca({
  id,
  corBase,
  metalico,
  rugosidade,
  ladoDuplo = false,
  acabamento = 'fosco',
}: {
  id: string
  corBase?: string
  metalico?: number
  rugosidade?: number
  ladoDuplo?: boolean
  acabamento?: Acabamento
}) {
  const aparencia = usePeca(id)
  const padrao = ACABAMENTOS[acabamento]
  const comuns = {
    color: corBase ?? aparencia.cor,
    emissive: aparencia.emissivo,
    // o realce precisa vencer o reflexo novo do ambiente, senão peça
    // selecionada e peça vizinha passam a se parecer
    emissiveIntensity: aparencia.intensidadeEmissiva,
    transparent: aparencia.transparente,
    opacity: aparencia.opacidade,
    depthWrite: aparencia.escreverProfundidade,
    metalness: metalico ?? padrao.metalico,
    roughness: rugosidade ?? padrao.rugosidade,
    envMapIntensity: padrao.ambiente,
    side: ladoDuplo ? (2 as const) : (0 as const),
  }

  // material físico custa mais caro no shader; só a pintura precisa do verniz,
  // e são dezenas de peças didáticas que não precisam
  if (acabamento === 'pintura') {
    return <meshPhysicalMaterial {...comuns} clearcoat={1} clearcoatRoughness={0.06} />
  }
  return <meshStandardMaterial {...comuns} />
}

/** Vidro: sempre translúcido, mas ainda selecionável e sujeito ao estado. */
export function MaterialVidro({ id }: { id: string }) {
  const aparencia = usePeca(id)
  return (
    <meshStandardMaterial
      color="#9fd9f2"
      emissive={aparencia.selecionada ? '#ffffff' : '#1d4a63'}
      emissiveIntensity={aparencia.selecionada ? 0.5 : 0.1}
      transparent
      opacity={Math.min(aparencia.opacidade, aparencia.selecionada ? 0.5 : 0.24)}
      depthWrite={false}
      roughness={0.08}
      metalness={0.1}
      // sem transmissão: exigiria alvo de render e há muita superfície de vidro
      // por cena. O reflexo do ambiente já entrega a leitura de vidro de graça.
      envMapIntensity={1.6}
      side={2}
    />
  )
}
